import React, { useMemo } from 'react';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import ForecastComparisonChartUplot from '../../../components/ForecastComparisonChartUplot';
import AnimationCircle from '../../../components/AnimationCircle';
import { IconState } from '../../../components/IconState';
import { buildForecastsChartProps, hasForecastsData } from './forecastsChartProps';

// Forecasts content: renders the uPlot chart (same component as the main
// dashboard plot) for the selected challenge's data, or a loading / error /
// empty state. Role-aware via buildForecastsChartProps; `selectedForecasterIds`
// limits the individual lines (admin/MO forecaster picker; null = all).
export const ForecastsPanel = ({
  targetDayForecasts,
  sessionRawData,
  canViewOthersData,
  blurSensitive = false,
  selectedForecasterIds = null,
  loading = false,
  hasError = false,
  errorMessage = '',
  units = 'mw',
}) => {
  // Memoized: a fresh props object on every render would change the chart's
  // internal memo identities, making uplot-react destroy and recreate the
  // uPlot instance on any page re-render (e.g. URL replace-navigations) -
  // which silently discards the user's zoom/pan state.
  const chartProps = useMemo(
    () => buildForecastsChartProps(
      targetDayForecasts,
      sessionRawData,
      canViewOthersData,
      blurSensitive,
      selectedForecasterIds
    ),
    [targetDayForecasts, sessionRawData, canViewOthersData, blurSensitive, selectedForecasterIds]
  );

  let body;
  if (loading) {
    body = (
      <div className="flex items-center justify-center h-full">
        <AnimationCircle />
      </div>
    );
  } else if (hasError) {
    body = (
      <div className="h-full flex items-center justify-center text-gray-400">
        <IconState
          icon={Squares2X2Icon}
          title="Configuration Error"
          description={errorMessage || 'This session has missing configuration data.'}
          dashedBorder={false}
        />
      </div>
    );
  } else if (hasForecastsData(targetDayForecasts, sessionRawData)) {
    body = (
      <ForecastComparisonChartUplot
        {...chartProps}
        blurSensitive={blurSensitive}
        units={units}
      />
    );
  } else {
    body = (
      <div className="h-full flex items-center justify-center text-gray-400">
        <IconState
          icon={Squares2X2Icon}
          title="No Data"
          description="No forecast data available for this horizon."
          dashedBorder={false}
        />
      </div>
    );
  }

  // Size the chart off the viewport minus the page chrome (header, filter bar,
  // card padding, the Recent-days strip and margins ≈ 26rem) so the two columns
  // shrink on short viewports and the strip stays visible. A low floor lets it
  // keep shrinking; the cap keeps it from dominating tall screens. On wide
  // screens (2xl, e.g. large/4K monitors) the cap is raised so the chart can
  // grow to fill the extra vertical space instead of leaving an empty band.
  return (
    <div className="h-[320px] sm:h-[420px] lg:h-[calc(100dvh-26rem)] lg:min-h-[300px] lg:max-h-[620px] 2xl:max-h-[820px]">
      {body}
    </div>
  );
};
